import React from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { formatDistance } from "date-fns";
import { COMMUNITY_TYPE_TO_TEXT } from "@sscs/report/constants";
import { RiskStatistics } from "@sscs/report/ui";
import { SoftwareIdentity } from "@sscs/types";
import {
  Card,
  DataTable,
  DataTableColumn,
  ExternalLink,
  LongTextLabel,
  QueryResult,
  Table,
} from "@sscs/ui";
import { LockIcon as MUILockIcon } from "@sscs/ui/icons";
import { getQueryStatus, prettifyLargeCount } from "@sscs/utils";
import { useGetCommunityPackageQuery } from "Services";
import { CommunityArtifact, PopularityTopLimit, QuarantineReason } from "Types";
import CommunityDeploymentRiskLabel from "./CommunityDeploymentRiskLabel";
import styles from "./communityVersions.module.scss";
import {
  getCommunityPackageDetails,
  getCommunityUrl,
  getCommunityUrlProps,
  getDeploymentRisk,
  isSdlcRiskAssessment,
  isSupportedCommunity,
} from "./utils";

const POPULARITY_LABELS: Record<PopularityTopLimit, string> = {
  10: "10",
  100: "100",
  1000: "1K",
  10000: "10K",
  30000: "30K",
} as const;

export const QUARANTINE_REASON_LABEL: Record<QuarantineReason, string> = {
  "rl-analyst": "Analyst",
  "rl-scanner": "Scanner",
  "rl-heuristics": "Heuristics",
  "ai-analysis": "AI",
  community: "Community",
} as const;

const getVersionArtifact = (
  packageArtifact: string | null | undefined,
  versionArtifacts: CommunityArtifact[],
): string => {
  if (!packageArtifact) return "";

  const versionArtifact = versionArtifacts.find(
    (artifact) =>
      artifact.type === "linked-package" &&
      artifact.ref.includes(packageArtifact),
  );

  return versionArtifact ? versionArtifact.ref : "";
};

const COLUMNS: DataTableColumn[] = [
  { id: "status", label: "Status", width: "100px" },
  { id: "risk", label: "Deployment risk", width: "40%" },
  { id: "details", label: "More details in Community", width: "60%" },
];

type CommunityVersionsProps = {
  identity: SoftwareIdentity;
};

const CommunityVersions = ({
  identity,
}: CommunityVersionsProps): React.JSX.Element | null => {
  const sdlcCommunityPackage = getCommunityPackageDetails(identity.purl);
  const supportedCommunity = isSupportedCommunity(
    sdlcCommunityPackage?.community ??
      COMMUNITY_TYPE_TO_TEXT[identity.community].toLowerCase(),
  );
  const isValidPurl =
    sdlcCommunityPackage?.community && sdlcCommunityPackage?.name;

  const {
    data: ossData,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetCommunityPackageQuery(
    isValidPurl && supportedCommunity
      ? {
          purl: identity.purl,
        }
      : skipToken,
  );

  if (!supportedCommunity || !isValidPurl) {
    return null;
  }

  return (
    <QueryResult
      status={getQueryStatus({
        data: ossData,
        isLoading,
        isFetching,
        isError,
        error,
      })}
    >
      <Card.Card className={styles.card}>
        <Card.Title
          className={styles["header-container"]}
          details={[
            ossData && (
              <div key="package" className={styles["package-block"]}>
                {/* package artifact is a different format for Community,
                    so use Community version of it versus our sdlc one */}
                <ExternalLink
                  href={getCommunityUrl({
                    ...sdlcCommunityPackage,
                    artifact: sdlcCommunityPackage.artifact
                      ? ossData.community.package.identity.artifact
                      : "",
                  } as getCommunityUrlProps)}
                  className={styles["purl-link"]}
                >
                  <LongTextLabel
                    label={`${
                      sdlcCommunityPackage.namespace
                        ? `${sdlcCommunityPackage.namespace}/`
                        : ""
                    }${sdlcCommunityPackage.name} ${
                      sdlcCommunityPackage.version
                        ? sdlcCommunityPackage.version
                        : ""
                    }`}
                  />
                </ExternalLink>
                <div className={styles["tags-container"]}>
                  {ossData.community.package.key_project && (
                    <div className={styles["tag"]}>KEY PROJECT</div>
                  )}
                  {ossData.community.package.popularity ? (
                    <div className={styles["tag"]}>
                      TOP{" "}
                      {POPULARITY_LABELS[ossData.community.package.popularity]}
                    </div>
                  ) : null}
                  {ossData.community.package.total_downloads ? (
                    <div className={styles["tag"]}>
                      {prettifyLargeCount(
                        ossData.community.package.total_downloads,
                      )}
                      &nbsp;downloads
                    </div>
                  ) : null}
                </div>
              </div>
            ),
          ]}
          actions={
            ossData?.community.package.is_quarantined ? (
              <div>
                <div className={styles["quarantine-status-header"]}>
                  <MUILockIcon className={styles["quarantine-icon"]} />
                  <span>QUARANTINE</span>
                </div>
                <div className={styles.subline}>
                  Reason:{" "}
                  {
                    QUARANTINE_REASON_LABEL[
                      ossData.community.package.quarantine.reason
                    ]
                  }
                </div>
              </div>
            ) : undefined
          }
        >
          <span className={styles["title-block"]}>
            <span className={styles["live-dot"]}></span>
            <span className={styles["title-lines"]}>
              <span>Spectra Assure Community</span>
              <span className={styles.subline}>
                5 latest published versions
              </span>
            </span>
          </span>
        </Card.Title>
        <DataTable
          chrome="plain"
          columns={COLUMNS}
          empty={{ subject: "community data" }}
        >
          {ossData?.community.package.versions.map((versionItem) => (
            <Table.Row key={`package-version-${versionItem.version}`}>
              <Table.Cell>
                <CommunityDeploymentRiskLabel
                  status={getDeploymentRisk(versionItem.assessments).status}
                />
              </Table.Cell>
              <Table.Cell>
                {isSdlcRiskAssessment(versionItem.assessments) ? (
                  <RiskStatistics
                    risk={versionItem.assessments}
                    showStatusIcon={false}
                  />
                ) : (
                  <>
                    <strong>Repository: </strong>
                    {versionItem.assessments.repository.label}
                  </>
                )}
              </Table.Cell>
              <Table.Cell>
                <div className={styles["version-link"]}>
                  <ExternalLink
                    href={getCommunityUrl({
                      community: ossData.community.package.identity.community,
                      name: ossData.community.package.identity.package,
                      namespace: ossData.community.package.identity.namespace,
                      version: versionItem.version,
                      artifact: getVersionArtifact(
                        sdlcCommunityPackage.artifact,
                        versionItem.artifacts,
                      ),
                    })}
                  >
                    <LongTextLabel label={versionItem.version} />
                  </ExternalLink>
                  {versionItem.version ===
                    ossData.community.package.latest_version && (
                    <div className={styles["version-tag"]}>LATEST</div>
                  )}
                  {versionItem.version === sdlcCommunityPackage.version && (
                    <div className={styles["version-tag"]}>SELECTED</div>
                  )}
                </div>
                <span className={styles["helper-text"]}>
                  Published{" "}
                  {formatDistance(new Date(versionItem.published), new Date(), {
                    addSuffix: true,
                  })}
                </span>
              </Table.Cell>
            </Table.Row>
          ))}
        </DataTable>
      </Card.Card>
    </QueryResult>
  );
};

export default CommunityVersions;
